import { parseCSV } from '@/utils/csvParser';
import { emptyNormalized, type NormalizedImport, type NormalizedTask, type LifecycleStatus } from '../normalizedImport';

// Wrike CSV / Excel export parser.
// Wrike's "Export to Excel" produces columns like:
//   Title, Description, Assigned To, Author, Start Date, Finish Date, Duration,
//   Status, Importance / Priority, Parent Task, Predecessors, Effort, Time spent,
//   Custom Status, Custom Status Group, Folders, Tags, Permalink, ID
// We map them onto the NormalizedImport shape and translate Wrike's
// workflow status groups (Active / Completed / Deferred / Cancelled) into
// Workitect's lifecycle + progress model.

const WRIKE_STATUS_OVERRIDES: Record<string, { lifecycle: LifecycleStatus; progress: number; flagged?: boolean }> = {
  // Active group
  'new': { lifecycle: 'active', progress: 0 },
  'in progress': { lifecycle: 'active', progress: 50 },
  'in review': { lifecycle: 'active', progress: 80 },
  'on track': { lifecycle: 'active', progress: 50 },
  'at risk': { lifecycle: 'active', progress: 50, flagged: true },
  'off track': { lifecycle: 'active', progress: 50, flagged: true },
  // Completed group
  'completed': { lifecycle: 'completed', progress: 100 },
  'approved': { lifecycle: 'completed', progress: 100 },
  // Deferred group → on_hold
  'deferred': { lifecycle: 'on_hold', progress: 0 },
  'on hold': { lifecycle: 'on_hold', progress: 0 },
  'postponed': { lifecycle: 'on_hold', progress: 0 },
  // Cancelled group
  'cancelled': { lifecycle: 'cancelled', progress: 0 },
  'canceled': { lifecycle: 'cancelled', progress: 0 },
  'rejected': { lifecycle: 'cancelled', progress: 0 },

  // ---- Localized variants (Wrike ships in ~10 languages) ----
  // French
  'nouveau': { lifecycle: 'active', progress: 0 },
  'en cours': { lifecycle: 'active', progress: 50 },
  'en révision': { lifecycle: 'active', progress: 80 },
  'en revision': { lifecycle: 'active', progress: 80 },
  'terminé': { lifecycle: 'completed', progress: 100 },
  'termine': { lifecycle: 'completed', progress: 100 },
  'différé': { lifecycle: 'on_hold', progress: 0 },
  'differe': { lifecycle: 'on_hold', progress: 0 },
  'annulé': { lifecycle: 'cancelled', progress: 0 },
  'annule': { lifecycle: 'cancelled', progress: 0 },
  // German
  'neu': { lifecycle: 'active', progress: 0 },
  'in bearbeitung': { lifecycle: 'active', progress: 50 },
  'in prüfung': { lifecycle: 'active', progress: 80 },
  'in pruefung': { lifecycle: 'active', progress: 80 },
  'abgeschlossen': { lifecycle: 'completed', progress: 100 },
  'erledigt': { lifecycle: 'completed', progress: 100 },
  'aufgeschoben': { lifecycle: 'on_hold', progress: 0 },
  'storniert': { lifecycle: 'cancelled', progress: 0 },
  // Spanish
  'nuevo': { lifecycle: 'active', progress: 0 },
  'en curso': { lifecycle: 'active', progress: 50 },
  'en revisión': { lifecycle: 'active', progress: 80 },
  
  'completado': { lifecycle: 'completed', progress: 100 },
  'aplazado': { lifecycle: 'on_hold', progress: 0 },
  'cancelado': { lifecycle: 'cancelled', progress: 0 },
  // Portuguese
  'concluído': { lifecycle: 'completed', progress: 100 },
  'concluido': { lifecycle: 'completed', progress: 100 },
  // Russian
  'завершено': { lifecycle: 'completed', progress: 100 },
  'отменено': { lifecycle: 'cancelled', progress: 0 },
  // Japanese
  '完了': { lifecycle: 'completed', progress: 100 },
  'キャンセル': { lifecycle: 'cancelled', progress: 0 },
};

const WRIKE_IMPORTANCE_TO_FLAG: Record<string, boolean> = {
  'high': true,
  'critical': true,
};

export function parseWrikeCSV(csvText: string): NormalizedImport {
  const out = emptyNormalized('wrike');
  const rows = parseCSV(csvText);
  if (rows.length < 2) {
    out.warnings.push('Wrike export has no data rows');
    return out;
  }

  // Normalize headers: lowercase, trim, collapse internal whitespace, strip
  // surrounding quotes and trailing punctuation that some exporters add (e.g.
  // "Status:", '"Custom Status"', "Start Date *").
  const headers = rows[0].map((h) =>
    (h ?? '')
      .toLowerCase()
      .replace(/^["'\s]+|["'\s*:]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );

  // Deterministic header resolution.
  //
  // Two-pass priority:
  //   1. Walk the alias list in order — return the FIRST exact header match.
  //   2. If no alias matches exactly, walk again and return the FIRST substring
  //      match (alias ⊂ header) using whole-word boundaries so "status" doesn't
  //      hijack "task id" or "custom-status-group".
  //
  // This guarantees that ambiguous Wrike exports — where "Status",
  // "Workflow Status", and "Custom Status" can all coexist — always resolve
  // to the highest-priority alias regardless of the column order in the file.
  const idx = (...names: string[]) => {
    for (const n of names) {
      const exact = headers.indexOf(n);
      if (exact >= 0) return exact;
    }
    for (const n of names) {
      const re = new RegExp(`(^|[^a-z0-9])${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`);
      const partial = headers.findIndex((h) => re.test(h));
      if (partial >= 0) return partial;
    }
    return -1;
  };

  const cId = idx('task id', 'permalink id', 'wrike id', 'id');
  const cTitle = idx('title', 'task name', 'task title', 'name', 'subject');
  const cDesc = idx('description', 'notes', 'details');
  const cAssignee = idx('assigned to', 'assignees', 'assignee', 'responsible', 'owner');
  const cStart = idx('start date', 'planned start', 'start');
  const cEnd = idx('finish date', 'due date', 'end date', 'planned finish', 'finish', 'end', 'due');
  // Priority order is intentional: a user-defined "Custom Status" wins over
  // the workflow group ("Workflow Status"), which itself wins over the raw
  // "Status" column. "Custom Status Group" must NOT win over "Custom Status".
  const cStatus = idx('custom status', 'workflow status', 'task status', 'status');
  const cImportance = idx('importance', 'priority');
  const cParent = idx('parent task', 'parent task id', 'parent id', 'parent');
  const cPreds = idx('predecessors', 'predecessor', 'dependencies', 'depends on');
  const cFolders = idx('folders', 'folder', 'space', 'project');
  const cTags = idx('tags', 'labels');
  const cPermalink = idx('permalink', 'url', 'link');
  // Only explicitly marked custom fields are captured; unknown columns are not silently tags.
  const customColumns = rows[0].map((name,index) => ({name:name.trim(),index})).filter(c => /^(custom field|cf)\s*[:\-]/i.test(c.name));
  out.settings = {customFields:customColumns.map(c => ({name:c.name.replace(/^(custom field|cf)\s*[:\-]\s*/i,''),type:'text'}))};

  const projectExternalId = `wrike:project`;
  out.projects.push({
    externalId: projectExternalId,
    name: 'Wrike import',
    description: 'Imported from Wrike. Folders and Spaces are preserved as tags.',
  });

  const seenAssignees = new Map<string, string>();

  rows.slice(1).forEach((row, i) => {
    const title = (cTitle >= 0 ? row[cTitle] : row[0])?.trim();
    if (!title) return;

    const externalId = (cId >= 0 && row[cId]?.trim()) || `wrike:${i}`;
    const parentRaw = cParent >= 0 ? row[cParent]?.trim() : '';
    // Wrike "Parent Task" cells often contain the parent's title, not its id.
    // We pass it through verbatim — the commit step resolves by id then by name.
    const parentExternalId = parentRaw || null;

    const assigneeRaw = cAssignee >= 0 ? row[cAssignee]?.trim() : '';
    const assigneeIds: string[] = [];
    if (assigneeRaw) {
      assigneeRaw.split(/[;,]/).map((a) => a.trim()).filter(Boolean).forEach((a) => {
        const key = a.toLowerCase();
        if (!seenAssignees.has(key)) {
          const id = `assignee:wrike:${key}`;
          seenAssignees.set(key, id);
          out.assignees.push({ externalId: id, name: a });
        }
        assigneeIds.push(seenAssignees.get(key)!);
      });
    }

    const rawStatus = cStatus >= 0 ? row[cStatus]?.trim() : undefined;
    const importance = cImportance >= 0 ? row[cImportance]?.trim().toLowerCase() : '';
    const importanceFlag = importance ? !!WRIKE_IMPORTANCE_TO_FLAG[importance] : false;

    const folderTags = cFolders >= 0
      ? (row[cFolders] || '').split(/[;,>]/).map((t) => t.trim()).filter(Boolean)
      : [];
    const explicitTags = cTags >= 0
      ? (row[cTags] || '').split(/[;,]/).map((t) => t.trim()).filter(Boolean)
      : [];
    const tags = Array.from(new Set([...folderTags, ...explicitTags]));

    const permalink = cPermalink >= 0 ? row[cPermalink]?.trim() : '';

    const task: NormalizedTask = {
      externalId,
      parentExternalId,
      name: title,
      intent: cDesc >= 0 ? row[cDesc]?.trim() : undefined,
      assigneeExternalIds: assigneeIds.length ? assigneeIds : undefined,
      startDate: cStart >= 0 ? toIsoOrUndef(row[cStart]) : undefined,
      endDate: cEnd >= 0 ? toIsoOrUndef(row[cEnd]) : undefined,
      status: rawStatus,
      flagged: importanceFlag || undefined,
      tags: tags.length ? tags : undefined,
      customFields: Object.fromEntries(customColumns.filter(c => row[c.index]?.trim()).map(c => [c.name.replace(/^(custom field|cf)\s*[:\-]\s*/i,''),row[c.index].trim()])),
      attachments: permalink ? [{ url: permalink, name: 'Open in Wrike' }] : undefined,
      // CSV rows don't tell us the parent's kind directly. If a parent
      // reference exists in the file, it's another row in this same export,
      // which in Wrike is always a Task — so any task with parentExternalId
      // is a Subtask. Tasks without one sit directly under a Folder/Space.
      importMetadata: {
        source: 'wrike-csv',
        originalParentKind: parentExternalId ? 'Task' : 'Folder',
        originalParentRef: parentExternalId,
        customFieldProvenance: customColumns.filter(c => row[c.index]?.trim()).map(c => ({source:'wrike-csv',sourceField:c.name,value:row[c.index].trim(),sourceRow:i+2})),
      },
    };
    out.tasks.push(task);

    if (cPreds >= 0) {
      (row[cPreds] || '')
        .split(/[;,]/)
        .map((d) => d.trim())
        .filter(Boolean)
        .forEach((dep) => {
          // Wrike predecessor format is often "<id>FS" or just "<id>".
          const cleaned = dep.replace(/(FS|FF|SS|SF)\s*[+-]?\d*$/i, '').trim();
          if (cleaned) {
            out.dependencies.push({ fromExternalId: cleaned, toExternalId: externalId });
          }
        });
    }
  });

  if (out.tasks.length === 0) {
    out.warnings.push('No Wrike tasks were parsed — check that the export uses the standard column headers.');
  }
  return out;
}

export const wrikeStatusOverrides = WRIKE_STATUS_OVERRIDES;

function toIsoOrUndef(s?: string): string | undefined {
  if (!s) return undefined;
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}
